import React from 'react';
import { usePlan } from '../hooks/usePlan';
import { Link } from 'react-router-dom';

const PlanProtectedRoute = ({ children, requiredFeature, fallbackMessage, fallbackComponent }) => {
  const { hasFeature, loading, trialExpired, getDaysLeftInTrial, canAccessBusinessPage } = usePlan();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Si es para Business Page, usar la función específica
  if (requiredFeature === 'business_page') {
    if (!canAccessBusinessPage()) {
      if (trialExpired) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Prueba Gratuita Expirada</h2>
                <p className="text-gray-600 mb-6">
                  Tu período de prueba gratuita de 14 días ha terminado. Para continuar usando las funciones empresariales, necesitas actualizar a un plan de negocio.
                </p>
                <div className="space-y-3">
                  <Link 
                    to="/admin/plans" 
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-block"
                  >
                    Ver Planes de Negocio
                  </Link>
                  <Link 
                    to="/admin/dashboard" 
                    className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors inline-block"
                  >
                    Volver al Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h4m0 4h2m-6 0h4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Plan de Negocio Requerido</h2>
                <p className="text-gray-600 mb-6">
                  Esta funcionalidad está disponible solo para planes de negocio. Actualiza tu plan para acceder a la gestión de staff y recursos.
                </p>
                <div className="space-y-3">
                  <Link 
                    to="/admin/plans" 
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-block"
                  >
                    Actualizar a Plan de Negocio
                  </Link>
                  <Link 
                    to="/admin/dashboard" 
                    className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors inline-block"
                  >
                    Volver al Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      }
    }
  }

  // Para otras funcionalidades
  if (requiredFeature && !hasFeature(requiredFeature)) {
    if (fallbackComponent) {
      return fallbackComponent;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Funcionalidad Premium</h2>
            <p className="text-gray-600 mb-6">
              {fallbackMessage || 'Esta funcionalidad requiere un plan superior. Actualiza tu plan para acceder.'}
            </p>
            <div className="space-y-3">
              <Link 
                to="/admin/plans" 
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-block"
              >
                Ver Planes
              </Link>
              <Link 
                to="/admin/dashboard" 
                className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors inline-block"
              >
                Volver al Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si tiene acceso, mostrar el contenido
  return children;
};

// Componente para mostrar alertas de trial
export const TrialAlert = () => {
  const { getDaysLeftInTrial, isTrialPlan, trialExpired } = usePlan();
  
  const daysLeft = getDaysLeftInTrial();
  
  if (trialExpired) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">
              <span className="font-medium">Prueba gratuita expirada.</span>{' '}
              <Link to="/admin/plans" className="underline hover:text-red-800">
                Actualiza tu plan para continuar.
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  if (isTrialPlan() && daysLeft !== null && daysLeft <= 7) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <span className="font-medium">Te quedan {daysLeft} días de prueba gratuita.</span>{' '}
              <Link to="/admin/plans" className="underline hover:text-yellow-800">
                Actualiza tu plan ahora.
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default PlanProtectedRoute;